import { validateCpfCnpj } from './documentValidators';
import { isValidCepFormat } from '../../components/CepInput';

/**
 * Percorre os passos 1..(cutoff-1) e retorna o PRIMEIRO com campos faltantes.
 * Serve pra apontar exatamente onde o usuário precisa voltar quando o wizard
 * tenta salvar o rascunho no passo 10 mas o backend rejeitaria por causa de
 * um campo obrigatório deixado vazio num passo anterior (ex.: lat/long do
 * passo 6 se o geocoding falhou silenciosamente).
 *
 * @param {number} cutoff — passo atual (inclusive na varredura)
 * @param {object} businessForm — estado do form
 * @returns {{step: number, missing: string[]} | null}
 */
export const findFirstIncompleteStep = (cutoff, businessForm) => {
  for (let step = 1; step <= cutoff; step += 1) {
    const missing = getMissingFields(step, businessForm);
    if (missing.length > 0) return { step, missing };
  }
  return null;
};

/**
 * Verifica se um HTML de rich-text (Quill) tem conteúdo real.
 * Quill retorna `<p><br></p>` para um editor vazio — strip tags primeiro.
 */
export const hasRichTextContent = (html) => {
  if (!html) return false;
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, '').trim().length > 0;
};

/**
 * Retorna `true` se todos os campos obrigatórios do step atual estão preenchidos.
 * Espelho de `getMissingFields` — não use os dois em paralelo (fonte única de verdade).
 *
 * @param {number} currentStep — número da etapa (1..13)
 * @param {object} businessForm — estado atual do form
 */
export const validateCurrentStep = (currentStep, businessForm) => {
  switch (currentStep) {
    case 1: {
      const isCpfCnpjValid = businessForm.cnpj_cpf && validateCpfCnpj(businessForm.cnpj_cpf);
      return (
        isCpfCnpjValid &&
        businessForm.nome &&
        businessForm.descricao_curta &&
        hasRichTextContent(businessForm.descricao) &&
        businessForm.categoria
      );
    }
    case 2: {
      const temCnpj = businessForm.cnpj_cpf.replace(/\D/g, '').length === 14;
      if (temCnpj) return !!businessForm.tipo_empresa;
      const isRendaPrincipal = businessForm.situacao_renda === 'principal';
      if (isRendaPrincipal) {
        return (
          businessForm.situacao_renda &&
          businessForm.situacao_ocupacao &&
          businessForm.colaboradores_diretos &&
          businessForm.colaboradores_indiretos
        );
      }
      return businessForm.situacao_renda && businessForm.situacao_ocupacao;
    }
    case 3:
      return (
        businessForm.loja_fisica ||
        businessForm.atendimento_no_meu_local ||
        businessForm.atendimento_no_local_cliente ||
        businessForm.atendimento_com_agendamento ||
        businessForm.atendimento_online ||
        businessForm.delivery ||
        businessForm.retirada_no_local ||
        businessForm.entrega_combinada ||
        businessForm.itinerante
      );
    case 4:
      return businessForm.whatsapp;
    case 6: {
      // Regra do produto: TODOS os cadastros devem ter endereço no
      // município. Removidas as opções sem_endereco_fixo/domicilio/online
      // do formulário. Legado: se o rascunho salvo veio com um desses
      // tipos, forçamos que seja tratado como area_urbana.
      const tipoLoc = businessForm.tipo_localizacao || 'area_urbana';
      if (!businessForm.municipio_id) return false;
      // Coordenadas são obrigatórias — backend não aceita sem lat/long.
      const lat = Number(businessForm.latitude);
      const lng = Number(businessForm.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return false;
      if (tipoLoc === 'area_urbana') {
        return !!(
          businessForm.rua &&
          String(businessForm.numero || '').trim() &&  // exige número (aceita "s/n")
          (businessForm.territory_id || businessForm.bairro) &&
          isValidCepFormat(businessForm.cep)
        );
      }
      if (tipoLoc === 'distrito') {
        return !!(
          businessForm.distrito &&
          businessForm.rua &&
          String(businessForm.numero || '').trim() &&
          businessForm.municipio_id
        );
      }
      if (tipoLoc === 'comunidade_rural') {
        // Comunidades rurais frequentemente compartilham o CEP default da
        // cidade e endereços podem não ter logradouro formal — não
        // validamos CEP nem rua nem exigimos ponto de referência.
        return !!(
          (businessForm.territory_id || businessForm.comunidade) &&
          businessForm.municipio_id
        );
      }
      // Tipo desconhecido/legado → tratamos como incompleto.
      return false;
    }
    case 12:
      return businessForm.terms_manager_accepted;
    case 13:
      return businessForm.aceita_politica_privacidade && businessForm.aceita_termos_uso;
    default:
      return true;
  }
};

/**
 * Lista de labels legíveis dos campos que ainda estão faltando no step atual.
 * Usado para renderizar o painel vermelho "Preencha para continuar" abaixo do botão Próximo.
 * Retorna `[]` quando o step está válido.
 */
export const getMissingFields = (currentStep, businessForm) => {
  const m = [];
  switch (currentStep) {
    case 1:
      if (!businessForm.cnpj_cpf || !validateCpfCnpj(businessForm.cnpj_cpf)) m.push('CNPJ ou CPF válido');
      if (!businessForm.nome) m.push('Nome do negócio');
      if (!businessForm.descricao_curta) m.push('Descrição curta');
      if (!hasRichTextContent(businessForm.descricao)) m.push('Descrição detalhada');
      if (!businessForm.categoria) m.push('Categoria principal');
      break;
    case 2: {
      const temCnpj = businessForm.cnpj_cpf.replace(/\D/g, '').length === 14;
      if (temCnpj) {
        if (!businessForm.tipo_empresa) m.push('Tipo de empresa');
      } else {
        if (!businessForm.situacao_renda) m.push('Esse negócio é (renda principal/complementar)');
        if (!businessForm.situacao_ocupacao) m.push('Sua situação atual');
        if (businessForm.situacao_renda === 'principal') {
          if (!businessForm.colaboradores_diretos) m.push('Colaboradores diretos');
          if (!businessForm.colaboradores_indiretos) m.push('Colaboradores indiretos');
        }
      }
      break;
    }
    case 3:
      if (!validateCurrentStep(currentStep, businessForm)) m.push('Pelo menos uma forma de atendimento');
      break;
    case 4:
      if (!businessForm.whatsapp) m.push('WhatsApp');
      break;
    case 6: {
      const tipoLoc = businessForm.tipo_localizacao || 'area_urbana';
      if (!businessForm.municipio_id) m.push('Município');
      if (tipoLoc === 'area_urbana') {
        if (!isValidCepFormat(businessForm.cep)) m.push('CEP (8 dígitos)');
        if (!businessForm.rua) m.push('Rua / Logradouro');
        if (!String(businessForm.numero || '').trim()) m.push('Número (informe "s/n" se não tiver)');
        if (!businessForm.territory_id && !businessForm.bairro) m.push('Bairro');
      } else if (tipoLoc === 'distrito') {
        if (!businessForm.distrito) m.push('Distrito');
        if (!businessForm.rua) m.push('Rua / Logradouro');
        if (!String(businessForm.numero || '').trim()) m.push('Número (informe "s/n" se não tiver)');
      } else if (tipoLoc === 'comunidade_rural') {
        if (!businessForm.territory_id && !businessForm.comunidade) m.push('Comunidade');
        // ponto_referencia deixou de ser obrigatório em rural (iter 210).
      } else {
        m.push('Tipo de localização inválido (selecione Área urbana, Distrito ou Comunidade rural)');
      }
      // Backend exige coordenadas para poder salvar (BusinessCreate.latitude/longitude
      // são não-Optional). Se o geocoding falhou silenciosamente, apontar aqui evita
      // o 422 opaco lá na frente.
      const lat = Number(businessForm.latitude);
      const lng = Number(businessForm.longitude);
      if (!Number.isFinite(lat) || lat === 0) m.push('Coordenadas do endereço (recarregue este passo ou ajuste manualmente no mapa)');
      else if (!Number.isFinite(lng) || lng === 0) m.push('Coordenadas do endereço (recarregue este passo ou ajuste manualmente no mapa)');
      break;
    }
    case 12:
      if (!businessForm.terms_manager_accepted) m.push('Aceite do termo de gestão');
      break;
    case 13:
      if (!businessForm.aceita_politica_privacidade) m.push('Aceite da Política de Privacidade');
      if (!businessForm.aceita_termos_uso) m.push('Aceite dos Termos de Uso');
      break;
    default:
      break;
  }
  return m;
};
